import React, { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Bell, Cat, ChevronLeft, ChevronRight, Dog, Heart, LocateFixed, MapPin, Rabbit, Search, SlidersHorizontal, Turtle, X } from 'lucide-react';
import { createAnimalAlert, searchAnimals, type AnimalAlertFilters } from '../../api/animals';
import SkeletonGrid from '../../components/ui/SkeletonGrid';
import ErrorCard from '../../components/ui/ErrorCard';
import { toAbsoluteUrl } from '../../utils/media';
import { MPL, MPL_FONT_BODY, MPL_FONT_DISPLAY, sizeLabel, speciesLabel, statusLabel } from '../../styles/mypetlive';
import MobileBottomNav from '../../components/MobileBottomNav';
import { useAuthModal } from '../../context/AuthModalContext';
import { useAnimalFavorites } from '../../hooks/useAnimalFavorites';
import PublicHeader from '../../components/PublicHeader';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import {
  CalcoFilAdulto, CalcoFilBotonLupa, CalcoFilCachorro, CalcoFilCiudad, CalcoFilConejo, CalcoFilConvivencia, CalcoFilEdad, CalcoFilEspecie,
  CalcoFilGato, CalcoFilHembra, CalcoFilJoven, CalcoFilLupa, CalcoFilMacho, CalcoFilPerro, CalcoFilReptil, CalcoFilSenior, CalcoFilSexo, CalcoFilTamano,
} from '../../components/icons/calcados';

// Las mismas cuatro del filtro de arriba. Valores canónicos (el servidor casa
// también las variantes en español, ver utils/species.ts).
const speciesOptions = [
  { label: 'Gatos', value: 'cat', icon: Cat, tint: MPL.coral100, color: MPL.coral },
  { label: 'Perros', value: 'dog', icon: Dog, tint: MPL.teal100, color: MPL.teal },
  { label: 'Reptiles', value: 'reptile', icon: Turtle, tint: MPL.olive100, color: MPL.oliveDark },
  { label: 'Otros', value: 'other', icon: Rabbit, tint: MPL.gold100, color: MPL.goldDark },
];

const sizeOptions = [
  { label: 'Pequeño', value: 'small' },
  { label: 'Mediano', value: 'medium' },
  { label: 'Grande', value: 'large' },
];

const sexOptions = [
  { label: 'Hembra', value: 'female' },
  { label: 'Macho', value: 'male' },
];

const ageOptions = [
  { label: 'Cachorro', value: 'puppy' },
  { label: 'Joven', value: 'young' },
  { label: 'Adulto', value: 'adult' },
  { label: 'Senior', value: 'senior' },
];

// --- Piezas de los filtros (maqueta del 2 nov 2026) ---------------------------
const ICONO_ESPECIE: Record<string, React.ComponentType<{ size?: number }>> = { cat: CalcoFilGato, dog: CalcoFilPerro, reptile: CalcoFilReptil, other: CalcoFilConejo };
const ICONO_EDAD: Record<string, React.ComponentType<{ size?: number }>> = { puppy: CalcoFilCachorro, young: CalcoFilJoven, adult: CalcoFilAdulto, senior: CalcoFilSenior };

function FiltroSeccion({ icono, titulo, primero, children }: { icono: React.ReactNode; titulo: string; primero?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ borderTop: primero ? 0 : '1px solid #ECE8E0', padding: primero ? '6px 0 18px' : '18px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, fontSize: 14, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#4A524D' }}>
        {icono}{titulo}
      </div>
      {children}
    </div>
  );
}

function ChipFiltro({ icono, active, onClick, children }: { icono: React.ReactNode; active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 9, height: 46, padding: '0 18px 0 14px', borderRadius: 999, cursor: 'pointer',
        border: active ? '1.5px solid #0E5F5B' : '1px solid #E2DED5', background: active ? '#E4F2DE' : '#FFFFFF',
        color: '#1E2A27', font: 'inherit', fontSize: 16, fontWeight: active ? 800 : 500,
      }}>
      {icono}{children}
    </button>
  );
}

function Casilla({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activa}
      style={{ border: 0, background: 'none', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 14, font: 'inherit', fontSize: 16, cursor: 'pointer', color: '#1E2A27', textAlign: 'left' }}>
      <span style={{ width: 26, height: 26, borderRadius: 7, boxSizing: 'border-box', border: activa ? 0 : '1.5px solid #D8D3C6', background: activa ? '#0E5F5B' : '#FFFFFF', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        {activa && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </span>
      {children}
    </button>
  );
}

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? `1.5px solid ${MPL.teal}` : `1.5px solid ${MPL.border}`,
        background: active ? MPL.teal : MPL.card,
        color: active ? '#fff' : MPL.ink,
        borderRadius: 999,
        padding: '8px 14px',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export default function AnimalsPublicList() {
  const { openAuth } = useAuthModal();
  const { user } = useAuth();
  const favorites = useAnimalFavorites();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get('page') || '1');
  const limit = Number(sp.get('limit') || '12');
  const q = sp.get('q') || undefined;
  const species = sp.get('species') || undefined;
  // Enlaces viejos con «perro»/«gato»: la tarjeta activa se reconoce igual.
  const normalizedSpecies = species === 'perro' ? 'dog' : species === 'gato' ? 'cat' : species === 'reptil' ? 'reptile' : species;
  const size = (sp.get('size') as 'small' | 'medium' | 'large' | null) || undefined;
  const sex = (sp.get('sex') as 'male' | 'female' | null) || undefined;
  const city = sp.get('city') || undefined;
  const ageGroup = (sp.get('ageGroup') as 'puppy' | 'young' | 'adult' | 'senior' | null) || undefined;
  const goodWithChildren = sp.get('goodWithChildren') === 'true' || undefined;
  const goodWithDogs = sp.get('goodWithDogs') === 'true' || undefined;
  const goodWithCats = sp.get('goodWithCats') === 'true' || undefined;
  // Por defecto, los más cercanos primero. El servidor usa la ubicación que le demos
  // (coordenadas del móvil o un código postal/municipio) o la dirección del perfil; si
  // no sabe dónde está, devuelve `origen: null` y aquí se la pedimos.
  const sort = (sp.get('sort') as 'createdAt' | 'name' | 'age' | 'distance' | null) || 'distance';
  const dir = (sp.get('dir') as 'asc' | 'desc' | null) || (sort === 'name' || sort === 'distance' ? 'asc' : 'desc');
  const near = sp.get('near') || undefined;
  const lat = sp.get('lat') ? Number(sp.get('lat')) : undefined;
  const lng = sp.get('lng') ? Number(sp.get('lng')) : undefined;
  const [placeOpen, setPlaceOpen] = useState(false);
  const [placeDraft, setPlaceDraft] = useState('');
  const [locating, setLocating] = useState(false);

  const setPlace = (place: { near?: string; lat?: number; lng?: number }) => {
    const next = new URLSearchParams(sp);
    ['near', 'lat', 'lng'].forEach(key => next.delete(key));
    if (place.near) next.set('near', place.near);
    if (place.lat !== undefined && place.lng !== undefined) {
      next.set('lat', place.lat.toFixed(2));
      next.set('lng', place.lng.toFixed(2));
    }
    next.set('sort', 'distance');
    next.set('dir', 'asc');
    next.set('page', '1');
    setSp(next);
    setPlaceOpen(false);
  };

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Este dispositivo no nos da la ubicación. Escribe tu código postal o tu ciudad.');
      setPlaceOpen(true);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocating(false);
        setPlace({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        setLocating(false);
        toast.error('No hemos podido saber dónde estás. Escribe tu código postal o tu ciudad.');
        setPlaceOpen(true);
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 },
    );
  };

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSp(next, { replace: false });
  };

  // El buscador era un input NO controlado que solo aplicaba al pulsar Enter.
  // Ahora escribe en la URL con retardo, así se busca al teclear y borrar el
  // texto limpia el filtro sin tener que acordarse de "Limpiar".
  const [queryDraft, setQueryDraft] = useState(q || '');

  React.useEffect(() => {
    setQueryDraft(q || '');
  }, [q]);

  React.useEffect(() => {
    const draft = queryDraft.trim();
    if (draft === (q || '')) return;
    const timer = setTimeout(() => setFilter('q', draft), 350);
    return () => clearTimeout(timer);
    // setFilter se reconstruye en cada render; depender de él relanzaría el
    // temporizador en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDraft, q]);

  const clearFilters = () => {
    // El sitio desde el que busca no es un filtro: se conserva.
    const keep: Record<string, string> = { page: '1', limit: String(limit) };
    for (const key of ['near', 'lat', 'lng']) {
      const value = sp.get(key);
      if (value) keep[key] = value;
    }
    setSp(keep, { replace: false });
  };

  const setFilterPage = (p: number) => {
    const next = new URLSearchParams(sp);
    next.set('page', String(p));
    setSp(next, { replace: false });
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['animals-public', { q, species, size, sex, city, ageGroup, goodWithChildren, goodWithDogs, goodWithCats, sort, dir, page, limit, near, lat, lng, user: user?._id || null }],
    queryFn: () => searchAnimals({ q, species, size, sex, city, ageGroup, goodWithChildren, goodWithDogs, goodWithCats, sort, dir, page, limit, near, lat, lng }),
    placeholderData: keepPreviousData,
    staleTime: 20_000,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const origin = data?.origen;
  const pages = Math.max(1, Math.ceil(total / limit));
  // Cada filtro activo lleva su clave: el chip tiene X y la X quita ESE filtro.
  const activeFilters = [
    q && { key: 'q', label: `Búsqueda: ${q}` },
    species && { key: 'species', label: speciesLabel(species) },
    size && { key: 'size', label: sizeLabel(size) },
    sex && { key: 'sex', label: sex === 'female' ? 'Hembra' : 'Macho' },
    city && { key: 'city', label: city },
    ageGroup && { key: 'ageGroup', label: ageOptions.find(option => option.value === ageGroup)?.label || ageGroup },
    goodWithChildren && { key: 'goodWithChildren', label: 'Convive con niños' },
    goodWithDogs && { key: 'goodWithDogs', label: 'Convive con perros' },
    goodWithCats && { key: 'goodWithCats', label: 'Convive con gatos' },
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  const alertFilters: AnimalAlertFilters = {
    q,
    species,
    size,
    sex,
    city,
    ageGroup,
    goodWithChildren,
    goodWithDogs,
    goodWithCats,
  };

  const alertMutation = useMutation({
    mutationFn: () => createAnimalAlert(alertFilters),
    onSuccess: () => toast.success('Alerta guardada'),
    onError: () => toast.error('No se pudo guardar la alerta'),
  });

  const saveAlert = () => {
    if (!user) {
      openAuth({
        mode: 'register',
        message: 'Crea tu cuenta para guardar esta alerta.',
        onSuccess: () => alertMutation.mutate(),
      });
      return;
    }
    alertMutation.mutate();
  };

  return (
    <div style={{ fontFamily: MPL_FONT_BODY, background: MPL.bg, color: MPL.ink, minHeight: '100vh', overflowX: 'clip' }}>
      <style>{`
        .catalog-link{color:inherit;text-decoration:none;}
        .catalog-link:hover{color:${MPL.teal};}
        .catalog-card{transition:transform .18s ease, box-shadow .18s ease;}
        .catalog-card:hover{transform:translateY(-4px);box-shadow:0 1px 3px rgba(31,55,40,.06),0 22px 44px -24px rgba(31,55,40,.32) !important;}
        .catalog-grid{display:grid;grid-template-columns:260px minmax(0,1fr);gap:28px;box-sizing:border-box;width:100%;max-width:100vw;}
        .catalog-results{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px;width:100%;min-width:0;}
        .catalog-card{min-width:0;}
        .catalog-filter-trigger{display:none!important}
        .catalog-filter-close{display:none!important}
        @media (max-width: 980px){.catalog-results{grid-template-columns:repeat(2,minmax(0,1fr));}}
        @media (max-width: 720px){
          .catalog-grid{grid-template-columns:minmax(0,1fr)!important}
          .catalog-grid > section{min-width:0}
          .catalog-filter-trigger{display:flex!important}
          .catalog-filter-close{display:grid!important}
          .catalog-filter-overlay{display:block!important}
          .catalog-aside{display:none;position:fixed!important;inset:auto 0 0 0!important;top:auto!important;z-index:51!important;width:100%!important;box-sizing:border-box!important;max-height:92dvh!important;overflow-y:auto!important;border-radius:26px 26px 0 0!important;padding:18px 20px calc(18px + env(safe-area-inset-bottom))!important;box-shadow:0 -12px 40px -20px rgba(0,0,0,.35)}
          .catalog-aside.is-open{display:block!important}
        }
        @media (max-width: 640px){.catalog-results{grid-template-columns:minmax(0,1fr)!important;width:calc(100vw - 40px)!important;max-width:calc(100vw - 40px)!important;gap:14px}.catalog-hero{padding:22px 20px 16px!important}.catalog-search{align-items:stretch!important;flex-wrap:wrap}.catalog-search > label{flex-basis:100%!important}.catalog-grid{padding:0 20px 92px!important}.catalog-results .catalog-card{border-radius:16px!important}.catalog-results .catalog-card > a > div:first-child{height:190px!important}.catalog-results .catalog-card > a > div:last-child{padding:14px 16px 17px!important}}
      `}</style>

      <PublicHeader />

      <main style={{ width: '100%', maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box' }}>
        <header className="catalog-hero" style={{ width: '100%', boxSizing: 'border-box', padding: '42px 32px 28px' }}>
          <div className="mpl-crumbs" style={{ fontSize: 13, color: MPL.faint, fontWeight: 700, marginBottom: 8 }}>
            <Link to="/" className="catalog-link">Inicio</Link> / Compañeros
          </div>
          <h1 style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 40, fontWeight: 800, margin: '0 0 6px' }}>
            Compañeros en adopción
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <p style={{ fontSize: 16, color: MPL.muted, margin: 0, flex: '1 1 260px' }}>
              <strong style={{ color: MPL.ink }}>{total}</strong> compañeros buscando hogar.
            </p>
            {/* Favoritos ya no está en la barra de abajo (la ocupa Mensajes): se llega desde aquí. */}
            <Link to="/me/favorites" className="catalog-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 12, border: `1.5px solid ${MPL.border}`, background: '#fff', fontWeight: 800, fontSize: 14, color: MPL.ink, textDecoration: 'none' }}>
              <Heart size={16} fill={favorites.ids.length ? MPL.coral : 'none'} color={favorites.ids.length ? MPL.coral : 'currentColor'} />
              Favoritos{favorites.ids.length ? ` · ${favorites.ids.length}` : ''}
            </Link>
            {/* Animales que alguien ha encontrado y que buscan a su familia. */}
            <Link to="/encontrados" className="catalog-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 12, border: `1.5px solid ${MPL.border}`, background: '#fff', fontWeight: 800, fontSize: 14, color: MPL.ink, textDecoration: 'none' }}>
              🔎 Encontrados
            </Link>
          </div>

          {/* Lo primero que se elige: qué animal. Tarjetas con su dibujo; al
              pulsar la activa se quita el filtro. */}
          <div className="catalog-species" role="group" aria-label="Filtrar por especie" style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, maxWidth: 560 }}>
            {speciesOptions.map(option => {
              const Icon = option.icon;
              const active = normalizedSpecies === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter('species', active ? '' : option.value)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '12px 4px 10px', minWidth: 0, borderRadius: 16, cursor: 'pointer', font: 'inherit',
                    background: active ? option.tint : '#fff',
                    border: `1.5px solid ${active ? option.color : MPL.border}`,
                    boxShadow: active ? 'none' : '0 1px 3px rgba(31,55,40,.05)',
                    color: MPL.ink,
                  }}
                >
                  <span style={{ width: 46, height: 46, borderRadius: 14, background: option.tint, color: option.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={28} strokeWidth={1.9} />
                  </span>
                  <span style={{ fontSize: 13.5, fontWeight: 800 }}>{option.label}</span>
                </button>
              );
            })}
          </div>

          <div className="catalog-search" style={{ marginTop: 26, display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1.5px solid ${MPL.border}`, borderRadius: 14, padding: '0 18px' }}>
              <Search size={18} color={MPL.faint} />
              <input
                aria-label="Buscar animales"
                placeholder="Busca por nombre, raza o ciudad"
                value={queryDraft}
                onChange={e => setQueryDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') setFilter('q', queryDraft.trim());
                  if (e.key === 'Escape') setQueryDraft('');
                }}
                style={{ flex: 1, minWidth: 0, height: 46, border: 0, outline: 0, font: 'inherit', color: MPL.ink, background: 'transparent' }}
              />
              {queryDraft && (
                <button
                  type="button"
                  onClick={() => setQueryDraft('')}
                  aria-label="Borrar búsqueda"
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', color: MPL.faint, display: 'flex', padding: 4 }}
                >
                  <X size={16} />
                </button>
              )}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1.5px solid ${MPL.border}`, borderRadius: 14, padding: '0 14px', fontSize: 14.5, fontWeight: 700 }}>
              <span className="sr-only">Ordenar</span>
              <select
                aria-label="Ordenar compañeros"
                value={`${sort}:${dir}`}
                onChange={event => {
                  const [nextSort, nextDir] = event.target.value.split(':');
                  const next = new URLSearchParams(sp);
                  next.set('sort', nextSort);
                  next.set('dir', nextDir);
                  next.set('page', '1');
                  setSp(next);
                }}
                style={{ height: 46, border: 0, outline: 0, background: 'transparent', color: MPL.ink, font: 'inherit', fontWeight: 700 }}
              >
                <option value="distance:asc">Más cerca</option>
                <option value="createdAt:desc">Más recientes</option>
                <option value="name:asc">Nombre A-Z</option>
                <option value="age:asc">Menor edad</option>
                <option value="age:desc">Mayor edad</option>
              </select>
            </label>
            <button className="catalog-filter-trigger" type="button" onClick={() => setFiltersOpen(true)} style={{ alignItems: 'center', justifyContent: 'center', gap: 8, border: `1.5px solid ${MPL.border}`, background: '#fff', borderRadius: 14, padding: '0 15px', height: 48, font: 'inherit', fontWeight: 800, color: MPL.ink }}>
              <SlidersHorizontal size={17} color={MPL.teal} />
              Filtros{activeFilters.length ? ` (${activeFilters.length})` : ''}
            </button>
          </div>

          {sort === 'distance' && data && (
            <div className="catalog-place" style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 14.5, color: MPL.muted }}>
              {origin && !placeOpen ? (
                <>
                  <MapPin size={17} color={MPL.teal} />
                  <span>
                    Primero los más cerca de <strong style={{ color: MPL.ink }}>{origin.fuente === 'ubicacion' ? 'tu ubicación' : origin.etiqueta.replace(/ \(.*\)$/, '')}</strong>
                  </span>
                  <button type="button" onClick={() => { setPlaceDraft(''); setPlaceOpen(true); }} style={{ border: 0, background: 'transparent', color: MPL.teal, fontWeight: 800, font: 'inherit', cursor: 'pointer', padding: 0 }}>
                    Cambiar
                  </button>
                </>
              ) : (
                <>
                  {!origin && <span style={{ width: '100%', color: MPL.ink, fontWeight: 700 }}>¿Dónde estás? Te enseñamos primero los animales que tienes más cerca.</span>}
                  <button type="button" onClick={useMyLocation} disabled={locating} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: `1.5px solid ${MPL.teal}`, background: '#fff', color: MPL.tealDark, borderRadius: 12, padding: '0 14px', height: 40, font: 'inherit', fontWeight: 800, cursor: locating ? 'wait' : 'pointer' }}>
                    <LocateFixed size={16} /> {locating ? 'Buscando…' : 'Usar mi ubicación'}
                  </button>
                  <form
                    onSubmit={event => {
                      event.preventDefault();
                      const value = placeDraft.trim();
                      if (value) setPlace({ near: value });
                    }}
                    style={{ display: 'flex', gap: 8, flex: '1 1 220px' }}
                  >
                    <input
                      aria-label="Código postal o ciudad"
                      placeholder="o tu código postal / ciudad"
                      value={placeDraft}
                      onChange={event => setPlaceDraft(event.target.value)}
                      style={{ flex: 1, minWidth: 0, height: 40, border: `1.5px solid ${MPL.border}`, borderRadius: 12, padding: '0 12px', font: 'inherit', color: MPL.ink, background: '#fff' }}
                    />
                    <button type="submit" style={{ border: 0, background: MPL.teal, color: '#fff', borderRadius: 12, padding: '0 14px', height: 40, font: 'inherit', fontWeight: 800, cursor: 'pointer' }}>
                      Ver
                    </button>
                  </form>
                  {near && !origin && <span style={{ width: '100%', color: MPL.coral, fontSize: 13.5 }}>No reconocemos «{near}». Prueba con el código postal.</span>}
                </>
              )}
            </div>
          )}
        </header>

        <div className="catalog-grid" style={{ padding: '0 32px 56px' }}>
          {filtersOpen && <button className="catalog-filter-overlay" type="button" aria-label="Cerrar filtros" onClick={() => setFiltersOpen(false)} style={{ display: 'none', position: 'fixed', inset: 0, zIndex: 50, border: 0, background: 'rgba(31,55,40,.42)' }} />}
          {/* Filtros calcados de la maqueta del 2 nov 2026: en el móvil, hoja que sube
              desde abajo; en el ordenador, columna. Tamaños normales de iPhone. */}
          <aside className={`catalog-aside${filtersOpen ? ' is-open' : ''}`} style={{ background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 22, padding: '20px 22px 22px', position: 'sticky', top: 90, alignSelf: 'start' }}>
            <span className="catalog-filter-close" aria-hidden style={{ width: 44, height: 5, borderRadius: 99, background: '#D9D6CF', margin: '-6px auto 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 26, fontWeight: 800, color: '#17352D' }}>Filtros</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {activeFilters.length > 0 && (
                  <button type="button" onClick={clearFilters} style={{ border: 0, background: 'none', color: MPL.coral, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                    Limpiar
                  </button>
                )}
                <button className="catalog-filter-close" type="button" onClick={() => setFiltersOpen(false)} aria-label="Cerrar filtros" style={{ padding: 0, width: 42, height: 42, placeItems: 'center', border: `1px solid ${MPL.border}`, background: '#fff', borderRadius: 13, color: '#17352D' }}>
                  <X size={20} />
                </button>
              </div>
            </div>

            <FiltroSeccion icono={<CalcoFilEspecie size={20} />} titulo="Especie" primero>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {speciesOptions.map(option => {
                  const Icono = ICONO_ESPECIE[option.value];
                  return (
                    <ChipFiltro key={option.value} icono={Icono ? <Icono size={22} /> : null} active={normalizedSpecies === option.value} onClick={() => setFilter('species', normalizedSpecies === option.value ? '' : option.value)}>
                      {option.label}
                    </ChipFiltro>
                  );
                })}
              </div>
            </FiltroSeccion>

            <FiltroSeccion icono={<CalcoFilTamano size={20} />} titulo="Tamaño">
              <div style={{ display: 'grid', gap: 4 }}>
                {sizeOptions.map(option => (
                  <Casilla key={option.value} activa={size === option.value} onClick={() => setFilter('size', size === option.value ? '' : option.value)}>{option.label}</Casilla>
                ))}
              </div>
            </FiltroSeccion>

            <FiltroSeccion icono={<CalcoFilSexo size={20} />} titulo="Sexo">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {sexOptions.map(option => (
                  <ChipFiltro key={option.value} icono={option.value === 'female' ? <CalcoFilHembra size={21} /> : <CalcoFilMacho size={19} />} active={sex === option.value} onClick={() => setFilter('sex', sex === option.value ? '' : option.value)}>
                    {option.label}
                  </ChipFiltro>
                ))}
              </div>
            </FiltroSeccion>

            <FiltroSeccion icono={<CalcoFilCiudad size={20} />} titulo="Ciudad">
              <label style={{ position: 'relative', display: 'block' }}>
                <CalcoFilLupa size={19} style={{ position: 'absolute', left: 14, top: 13, pointerEvents: 'none' }} />
                <input
                  type="search"
                  defaultValue={city || ''}
                  placeholder="Madrid, Valencia..."
                  aria-label="Ciudad"
                  onKeyDown={event => {
                    if (event.key === 'Enter') setFilter('city', event.currentTarget.value.trim());
                  }}
                  onBlur={event => { if (event.currentTarget.value.trim() !== (city || '')) setFilter('city', event.currentTarget.value.trim()); }}
                  style={{ width: '100%', boxSizing: 'border-box', height: 46, border: `1px solid ${MPL.border}`, borderRadius: 14, padding: '0 14px 0 44px', color: MPL.ink, font: 'inherit', fontSize: 16, outline: 0 }}
                />
              </label>
            </FiltroSeccion>

            <FiltroSeccion icono={<CalcoFilEdad size={20} />} titulo="Edad">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ageOptions.map(option => {
                  const Icono = ICONO_EDAD[option.value];
                  return (
                    <ChipFiltro key={option.value} icono={Icono ? <Icono size={20} /> : null} active={ageGroup === option.value} onClick={() => setFilter('ageGroup', ageGroup === option.value ? '' : option.value)}>
                      {option.label}
                    </ChipFiltro>
                  );
                })}
              </div>
            </FiltroSeccion>

            <FiltroSeccion icono={<CalcoFilConvivencia size={20} />} titulo="Convivencia">
              <div style={{ display: 'grid', gap: 4 }}>
                {[
                  ['goodWithChildren', 'Con niños', goodWithChildren],
                  ['goodWithDogs', 'Con perros', goodWithDogs],
                  ['goodWithCats', 'Con gatos', goodWithCats],
                ].map(([key, label, active]) => (
                  <Casilla key={String(key)} activa={Boolean(active)} onClick={() => setFilter(String(key), active ? '' : 'true')}>{String(label)}</Casilla>
                ))}
              </div>
            </FiltroSeccion>

            <button type="button" onClick={() => setFiltersOpen(false)} className="catalog-filter-trigger" style={{ width: '100%', height: 54, marginTop: 22, alignItems: 'center', justifyContent: 'center', gap: 12, border: 0, borderRadius: 16, background: '#0E5F5B', color: '#fff', font: 'inherit', fontSize: 17.5, fontWeight: 800 }}>
              <CalcoFilBotonLupa size={22} color="#FFFFFF" />
              Ver {total} {total === 1 ? 'compañero' : 'compañeros'}
            </button>
          </aside>

          <section>
            {activeFilters.length > 0 && (
              <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
                <span style={{ fontSize: 13, color: MPL.faint, fontWeight: 700 }}>Activos:</span>
                {activeFilters.map(filter => (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => setFilter(filter.key, '')}
                    aria-label={`Quitar filtro ${filter.label}`}
                    title="Quitar filtro"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: MPL.teal100, color: MPL.tealDark, fontSize: 13, fontWeight: 700, padding: '6px 13px', borderRadius: 999, border: 0, font: 'inherit', cursor: 'pointer' }}
                  >
                    {filter.label}
                    <X size={13} />
                  </button>
                ))}
                {isFetching && <span style={{ fontSize: 13, color: MPL.faint }}>Actualizando...</span>}
                <button type="button" onClick={saveAlert} disabled={alertMutation.isPending} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, border: `1.5px solid ${MPL.teal}`, background: '#fff', color: MPL.teal, borderRadius: 12, padding: '8px 12px', font: 'inherit', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                  <Bell size={15} />
                  {alertMutation.isPending ? 'Guardando...' : 'Crear alerta'}
                </button>
              </div>
            )}

            {isLoading ? (
              <SkeletonGrid />
            ) : isError ? (
              <ErrorCard message="No pudimos cargar los animales. Comprueba tu conexión e inténtalo de nuevo." onRetry={() => { void refetch(); }} />
            ) : items.length === 0 ? (
              <div style={{ background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 20, padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 38, marginBottom: 8 }}>🐾</div>
                <div style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 20, fontWeight: 800, marginBottom: 6 }}>
                  No encontramos compañeros con esos filtros
                </div>
                <p style={{ color: MPL.muted, fontSize: 14, margin: '0 0 18px' }}>
                  Prueba a quitar algún filtro, o te avisamos cuando llegue uno que encaje.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {activeFilters.length > 0 && (
                    <>
                      <button type="button" onClick={clearFilters} style={{ background: MPL.teal, color: '#fff', border: 0, borderRadius: 13, padding: '12px 18px', font: 'inherit', fontWeight: 800, cursor: 'pointer' }}>
                        Quitar filtros
                      </button>
                      <button type="button" onClick={saveAlert} disabled={alertMutation.isPending} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#fff', color: MPL.teal, border: `1.5px solid ${MPL.teal}`, borderRadius: 13, padding: '12px 18px', font: 'inherit', fontWeight: 800, cursor: 'pointer' }}>
                        <Bell size={15} /> {alertMutation.isPending ? 'Guardando…' : 'Avisadme cuando llegue'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="catalog-results">
                  {items.map((a: any) => {
                    const id = String(a._id || a.id);
                    const image = Array.isArray(a.images) ? a.images[0] : null;
                    const personality = Array.isArray(a.personality) ? a.personality.slice(0, 2) : [];
                    const distance = typeof a.distanceKm === 'number' ? `a ${a.distanceKm} km` : null;
                    const meta = [speciesLabel(a.species), sizeLabel(a.size), a.age, a.city, distance].filter(Boolean).join(' · ');
                    const favorite = favorites.isFavorite(id);
                    return (
                      <article key={id} className="catalog-card" style={{ position: 'relative', background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 1px 3px rgba(31,55,40,.06),0 8px 24px -16px rgba(31,55,40,.18)' }}>
                        <Link to={`/animals/${id}`} style={{ display: 'block', textDecoration: 'none', color: MPL.ink }}>
                          <div style={{ height: 200, background: '#E6E0D2', position: 'relative', overflow: 'hidden' }}>
                            {image ? (
                              <img src={toAbsoluteUrl(image)} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MPL.faint, fontSize: 13 }}>Sin imagen</div>
                            )}
                            <span style={{ position: 'absolute', top: 12, left: 12, background: a.status === 'publicado' ? MPL.teal100 : MPL.gold100, color: a.status === 'publicado' ? MPL.tealDark : MPL.goldDark, fontSize: 11.5, fontWeight: 800, padding: '5px 11px', borderRadius: 8 }}>
                              {a.status === 'publicado' ? (a.createdByRole === 'particular' ? 'Lo cede un particular' : 'Disponible') : statusLabel(a.status)}
                            </span>
                          </div>
                          <div style={{ padding: '16px 18px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                              <span style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 21, fontWeight: 800 }}>{a.name}</span>
                              {a.code && <span style={{ fontSize: 12, color: MPL.olive, fontWeight: 800 }}>{a.code}</span>}
                            </div>
                            <div style={{ fontSize: 13, color: MPL.muted, margin: '4px 0 12px' }}>{meta || 'Información pendiente'}</div>
                            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                              {(personality.length ? personality : ['Sociable']).map((trait: string) => (
                                <span key={trait} style={{ background: MPL.bg, color: MPL.muted, fontSize: 11.5, fontWeight: 700, padding: '5px 11px', borderRadius: 999 }}>
                                  {trait}
                                </span>
                              ))}
                            </div>
                          </div>
                        </Link>
                        <button
                          type="button"
                          aria-label={favorite ? `Quitar ${a.name} de favoritos` : `Añadir ${a.name} a favoritos`}
                          title={favorite ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                          onClick={() => favorites.toggle(id)}
                          disabled={favorites.isPending(id)}
                          style={{ padding: 0, position: 'absolute', top: 12, right: 12, width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 13, border: 0, background: 'rgba(255,255,255,.94)', color: favorite ? MPL.coral : MPL.muted, boxShadow: '0 6px 18px -8px rgba(31,55,40,.4)', cursor: 'pointer' }}
                        >
                          <Heart size={20} fill={favorite ? 'currentColor' : 'none'} />
                        </button>
                      </article>
                    );
                  })}
                </div>

                <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 32 }} aria-label="Paginación">
                  <button type="button" onClick={() => setFilterPage(Math.max(1, page - 1))} disabled={page <= 1} aria-label="Página anterior" style={{ width: 40, height: 40, borderRadius: 12, background: '#fff', border: `1px solid ${MPL.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MPL.muted, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? .5 : 1 }}>
                    <ChevronLeft size={17} />
                  </button>
                  {/* Ventana alrededor de la página actual (antes solo se veían la 1-3). */}
                  {Array.from({ length: Math.min(3, pages) }, (_, i) => Math.max(1, Math.min(page - 1, pages - 2)) + i).map(n => (
                    <button key={n} type="button" onClick={() => setFilterPage(n)} aria-current={page === n ? 'page' : undefined} style={{ padding: 0, width: 40, height: 40, borderRadius: 12, background: page === n ? MPL.teal : '#fff', color: page === n ? '#fff' : MPL.muted, border: page === n ? `1px solid ${MPL.teal}` : `1px solid ${MPL.border}`, fontWeight: 800, cursor: 'pointer' }}>
                      {n}
                    </button>
                  ))}
                  {pages > 3 && (
                    <span style={{ fontSize: 13, color: MPL.faint, fontWeight: 700, padding: '0 4px' }}>de {pages}</span>
                  )}
                  <button type="button" onClick={() => setFilterPage(Math.min(pages, page + 1))} disabled={page >= pages} aria-label="Página siguiente" style={{ padding: 0, width: 40, height: 40, borderRadius: 12, background: '#fff', border: `1px solid ${MPL.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MPL.muted, cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? .5 : 1 }}>
                    <ChevronRight size={17} />
                  </button>
                </nav>
              </>
            )}
          </section>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
}
